/**
 * Detect in-app browsers (Facebook Messenger, Instagram, Facebook app, TikTok, etc.).
 * Google's OAuth blocks sign-in from these "webviews" with `disallowed_useragent`,
 * so we need to warn the user and offer a way to open the page in a real browser.
 */

const IN_APP_PATTERNS = [
  // Meta family
  /FBAN|FBAV|FB_IAB|FBIOS|FBSS/i,        // Facebook app
  /Messenger/i,                            // FB Messenger
  /Instagram/i,                            // Instagram
  /Threads/i,                              // Threads
  // Other social/messaging webviews
  /MicroMessenger/i,                       // WeChat
  /Line\//i,                               // LINE
  /TikTok|musical_ly/i,                    // TikTok
  /Twitter|TwitterAndroid/i,               // X / Twitter
  /Snapchat/i,                             // Snapchat
  /WhatsApp/i,                             // WhatsApp
  /VKAndroid|VKiOS/i,                      // VK
  /OdnoklassnikiAndroid|OdnoklassnikiIOS/i, // OK.ru
];

export function detectInAppBrowser() {
  if (typeof navigator === "undefined") {
    return { isInApp: false, app: null, platform: "unknown" };
  }
  const ua = navigator.userAgent || "";

  let app = null;
  for (const rx of IN_APP_PATTERNS) {
    const m = ua.match(rx);
    if (m) { app = m[0]; break; }
  }

  // Crude platform detection — enough to pick the right "open in Safari/Chrome"
  // escape hatch (iOS uses `x-safari-https://`, Android uses `intent://`).
  let platform = "unknown";
  if (/iPad|iPhone|iPod/.test(ua)) platform = "ios";
  else if (/Android/.test(ua)) platform = "android";
  else platform = "desktop";

  return { isInApp: !!app, app, platform };
}

/**
 * Try to escape the in-app webview to the system browser.
 * - iOS: open via `x-safari-https://` URL scheme.
 * - Android: use `intent://` with browser fallback.
 * - Other: just window.open as a best-effort.
 */
export function openInSystemBrowser(url) {
  if (typeof window === "undefined") return;
  const { platform } = detectInAppBrowser();
  const stripped = url.replace(/^https?:\/\//, "");

  if (platform === "ios") {
    window.location.href = `x-safari-https://${stripped}`;
    return;
  }
  if (platform === "android") {
    // `intent://` lets Android pick a real browser; `S.browser_fallback_url`
    // is a safe HTTPS fallback if the intent isn't honored.
    window.location.href =
      `intent://${stripped}#Intent;scheme=https;` +
      `S.browser_fallback_url=${encodeURIComponent(url)};end`;
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
